import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {EntityWithTextAndSuspended, MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import ErrorMessage from 'ui-library/ErrorMessage';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';

interface PartiesFieldProps {
  value: Array<MinimalEntity>;
  onChange: (value: Array<MinimalEntity>) => void;
  isBeingCreated: boolean;
  label: string;
  showError: boolean;
  setDoesNotHaveUnresolvedInput?: (value: boolean) => void;
  errorText?: string;
  isDisabled?: boolean;
  dataTestAttrs?: DataTestAttrs;
  customViewerValue?: string | JSX.Element;
}

/**
 * Field to select or modify a list of parties. Renders EntityInputWithSearch when isBeingCreated is true and
 * EditablePartyField otherwise.
 */
export default function PartiesField(props: PartiesFieldProps): JSX.Element {
  const componentId = useRef(_.uniqueId());
  const entityToMinimalEntity = (entities: Array<EntityWithTextAndSuspended>): Array<MinimalEntity> =>
    entities ? entities.map((entity) => ({id: entity.id, handle: entity.handle})) : [];

  const sharedInputProps = {
    enableNewEntityInput: true,
    allowEmails: true,
    allowTeams: true,
    allowOrganizations: false,
    placeholder: 'Search for user or team',
    shouldAllowMultiple: true as const, // explicitly casting to true because PartyProps expects true (not boolean)
    userSearchType: UserSearchType.All,
  };
  assert(
    !props.isBeingCreated || (props.errorText != null && props.setDoesNotHaveUnresolvedInput != null),
    'errorText and setDoesNotHaveUnresolvedInput props must be provided if isBeingCreated is true'
  );

  return (
    <>
      <InputWrapper
        id={componentId.current}
        label={props.label}
        labelStyle={FieldLabel.STYLES.LABEL}
        isRequired={false}
      >
        {props.isBeingCreated ? (
          <EntityInputWithSearch
            {...sharedInputProps}
            onChange={(entityArray: Array<EntityWithTextAndSuspended>) => {
              props.onChange(entityToMinimalEntity(entityArray));
            }}
            onChangeInput={(value) => {
              props.setDoesNotHaveUnresolvedInput!(value === '');
            }}
            value={props.value}
            size={TagsInputSize.SMALL}
            className={css(styles.inputBox)}
            emptyText="No results found"
          />
        ) : (
          <EditablePartyField
            {...sharedInputProps}
            isFullWidth={true}
            value={props.value}
            initialIsBeingEdited={false}
            isDisabled={Boolean(props.isDisabled)}
            onChange={(changedValue: Array<MinimalEntity>) => {
              props.onChange(changedValue);
            }}
            allowEmptyValue={true}
            chipSize={TagsInputSize.SMALL}
            dataTestAttrs={props.dataTestAttrs}
            customViewerValue={props.customViewerValue}
          />
        )}
      </InputWrapper>
      {props.showError && <ErrorMessage error={props.errorText ?? null} />}
    </>
  );
}

const styles = StyleSheet.create({
  inputBox: {
    flex: 1,
  },
});
